import React from 'react';
import { Link } from 'react-router-dom';
import { useLocaleContext } from '../../context/LocaleContext';
import { locales, urlMappings } from '../../utils/hreflang';
import { apiNavItems } from '../../utils/navigationData';
import { getTranslation } from '../../utils/translations';
import * as Icons from 'lucide-react';
import NavDropdown from './NavDropdown';

const ApiDropdown: React.FC = () => {
  const { locale } = useLocaleContext();

  // Get locale-aware path (same pattern as Footer)
  const getLocalePath = (path: string) => {
    const prefix = locales[locale] || '';
    const mappings = urlMappings[path];
    if (mappings && mappings[locale]) {
      return `${prefix}${mappings[locale]}`;
    }
    return `${prefix}${path}`;
  };

  // Get icon component dynamically
  const getIcon = (iconName: string) => {
    const IconComponent = (Icons as any)[iconName];
    return IconComponent ? <IconComponent size={18} aria-hidden="true" /> : null;
  };

  // Translation key mapping for titles and descriptions
  const translationKeys: Record<string, { title: string; desc: string }> = {
    'API Overview': { title: 'nav.apiOverview', desc: 'nav.apiOverviewDesc' },
    'Developer Docs': { title: 'nav.developerDocs', desc: 'nav.developerDocsDesc' },
    'For Writing Tools': { title: 'nav.forWritingTools', desc: 'nav.forWritingToolsDesc' },
    'For Chatbots': { title: 'nav.forChatbots', desc: 'nav.forChatbotsDesc' },
    'For Content Platforms': { title: 'nav.forContentPlatforms', desc: 'nav.forContentPlatformsDesc' },
  };

  return (
    <NavDropdown trigger={getTranslation(locale, 'nav.api')} width="w-72" align="left">
      <div className="py-1">
        {apiNavItems.map((item, index) => {
          const translationKey = translationKeys[item.title];
          const title = translationKey ? getTranslation(locale, translationKey.title) : item.title;
          const description = translationKey ? getTranslation(locale, translationKey.desc) : item.description;

          return (
            <Link
              key={index}
              to={getLocalePath(item.path)}
              className="flex items-center gap-3 px-4 py-2.5 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors group"
              role="menuitem"
            >
              <div className="flex-shrink-0 text-neutral-600 dark:text-neutral-400 group-hover:text-accent-600 dark:group-hover:text-accent-400 transition-colors">
                {getIcon(item.icon || 'Circle')}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-neutral-900 dark:text-white group-hover:text-accent-600 dark:group-hover:text-accent-400 transition-colors">
                  {title}
                </div>
                {description && (
                  <div className="text-xs text-neutral-600 dark:text-neutral-400 mt-0.5">
                    {description}
                  </div>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </NavDropdown>
  );
};

export default React.memo(ApiDropdown);
